import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Download,
  Share2,
  ExternalLink,
  Eye,
  CheckCircle,
  Calendar,
  Award,
  BookOpen,
  GraduationCap,
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';
import { checkSessionExpired } from '../lib/api';

// EU Flag Icon component - 12 gold stars in circle on blue background
function EUFlagIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" fill="#003399"/>
      {/* 12 stars in a circle */}
      {[...Array(12)].map((_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const x = 12 + 7 * Math.cos(angle);
        const y = 12 + 7 * Math.sin(angle);
        return (
          <polygon
            key={i}
            points="0,-1.2 0.35,-0.35 1.14,-0.37 0.5,0.2 0.7,1 0,0.5 -0.7,1 -0.5,0.2 -1.14,-0.37 -0.35,-0.35"
            fill="#FFCC00"
            transform={`translate(${x},${y}) scale(1.1)`}
          />
        );
      })}
    </svg>
  );
}

interface Certificate {
  id: string;
  certificate_number: string;
  title: string;
  description: string;
  learning_outcomes: any;
  ects_credits: number;
  competency_points: number;
  eqf_level: number;
  assessment_result: {
    score: number;
    passed: boolean;
  };
  course_completion_date: string;
  exam_date: string;
  issue_date: string;
  valid_until: string | null;
  sensor_name: string;
  // v6.7.0 — Vurderingsform (SKOS-slug + prefLabel)
  assessment_form_slug?: string | null;
  assessment_form_label?: string | null;
  assessment_form_label_en?: string | null;
  assessment_form_uri?: string | null;
  status: 'active' | 'revoked' | 'expired';
  verification_code: string;
  verification_url: string;
  public_share_code: string | null;
  share_enabled: boolean;
  view_count: number;
  verification_count: number;
  last_verified_at: string | null;
  member_name: string;
  association_name: string;
  created_at: string;
  updated_at: string;
  // Task #192: Modul-subset (NULL = hele studieplanen, fylt = subset)
  module_ids?: string[] | null;
  modules?: any;
}

export function CertificateDetailPage() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [europassLoading, setEuropassLoading] = useState(false);
  const [europassSuccess, setEuropassSuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [googleWalletLoading, setGoogleWalletLoading] = useState(false);
  const [appleWalletLoading, setAppleWalletLoading] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  // v6.7.0 — kandidatens språkpreferanse (brukes for PDF/EDC-nedlasting)
  const [candidateLanguage, setCandidateLanguage] = useState<'no' | 'en'>('no');

  useEffect(() => {
    fetchCertificate();
    // Hent profil for å vite valgt språk (fail-safe, defaulter til norsk)
    fetch('/api/candidate/profile', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.profile?.language === 'en') setCandidateLanguage('en');
      })
      .catch(() => {}); // Ignorer feil, bruk default
  }, [certificateId]);

  async function fetchCertificate() {
    try {
      setLoading(true);
      const res = await fetch(`/api/candidate/certificates/${certificateId}`, {
        credentials: 'include'
      });

      // Check for session expiration (redirects to login if expired)
      await checkSessionExpired(res);

      if (!res.ok) {
        throw new Error('Kunne ikke hente attestdetaljer');
      }

      const data = await res.json();
      setCertificate(data.certificate);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPDF() {
    try {
      setPdfLoading(true);
      const res = await fetch(`/api/candidate/certificates/${certificateId}/pdf`, {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        throw new Error('Kunne ikke laste ned PDF');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${certificate?.certificate_number || 'attest'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Kunne ikke laste ned PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  }

  async function handleToggleShare() {
    if (!certificate) return;

    try {
      setShareLoading(true);
      const res = await fetch(`/api/candidate/certificates/${certificateId}/share`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ share_enabled: !certificate.share_enabled })
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        throw new Error('Kunne ikke oppdatere delingsinnstillinger');
      }

      const data = await res.json();
      setCertificate({
        ...certificate,
        share_enabled: data.share_enabled,
        public_share_code: data.public_share_code
      });
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setShareLoading(false);
    }
  }

  function handleCopyShareLink() {
    if (!certificate?.public_share_code) return;

    const shareUrl = `${window.location.origin}/share/${certificate.public_share_code}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleOpenVerification() {
    if (!certificate) return;
    window.open(certificate.verification_url, '_blank');
  }

  async function handleOpenEDCViewer() {
    try {
      // Show loading overlay immediately
      setViewerLoading(true);

      // v6.7.0 — send language-param så EDCI Viewer-bildet rendres på rett språk
      const res = await fetch(`/api/europass-edci/${certificateId}/signed-credential?language=${candidateLanguage}`, {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        throw new Error('Kunne ikke generere credential');
      }

      // Get credential as text and encode to base64
      const credentialText = await res.text();
      const base64Credential = btoa(unescape(encodeURIComponent(credentialText)));

      // Navigate to EDCI Viewer with base64-encoded credential
      const viewerUrl = `https://visning.kompetanseattest.no/europass2/edci-viewer/#/auto-load#${base64Credential}`;

      // Open in new tab and hide spinner
      window.open(viewerUrl, '_blank');
      setViewerLoading(false);
    } catch (err: any) {
      setViewerLoading(false);
      alert('Kunne ikke åpne EDCI Viewer: ' + err.message);
    }
  }

  async function handleDownloadSignedEuropass() {
    try {
      setEuropassLoading(true);
      setEuropassSuccess(false);

      const res = await fetch(`/api/europass-edci/${certificateId}/signed-credential?language=${candidateLanguage}`, {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        throw new Error('Kunne ikke laste ned signert Europass-credential');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Europass_${certificate?.certificate_number || 'attest'}.jsonld`;
      document.body.appendChild(a);

      // Show success message first, then trigger download
      setEuropassSuccess(true);
      setEuropassLoading(false);

      // Trigger download after showing success
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Hide success message after 10 seconds
      setTimeout(() => {
        setEuropassSuccess(false);
      }, 10000);
    } catch (err: any) {
      setEuropassLoading(false);
      setEuropassSuccess(false);
      alert('Kunne ikke laste ned signert Europass-credential: ' + err.message);
    }
  }

  async function handleGoogleWallet() {
    try {
      setGoogleWalletLoading(true);
      const res = await fetch(`/api/candidate/certificates/${certificateId}/google-wallet`, {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Kunne ikke generere Google Wallet-pass');
      }

      const data = await res.json();

      // Open Google Wallet save URL in new tab
      if (data.saveUrl) {
        window.open(data.saveUrl, '_blank');
      } else {
        throw new Error('Ingen Google Wallet URL mottatt');
      }
    } catch (err: any) {
      alert('Feil ved Google Wallet: ' + err.message);
    } finally {
      setGoogleWalletLoading(false);
    }
  }

  async function handleAppleWallet() {
    try {
      setAppleWalletLoading(true);
      const res = await fetch(`/api/candidate/certificates/${certificateId}/apple-wallet`, {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Kunne ikke generere Apple Wallet-pass');
      }

      // Get the .pkpass file as blob
      const blob = await res.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${certificate?.certificate_number || 'kompetanseattest'}.pkpass`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Feil ved Apple Wallet: ' + err.message);
    } finally {
      setAppleWalletLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto"></div>
          <p className="mt-4 text-kf-gray-600">Laster attest...</p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="w-16 h-16 text-kf-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-kf-gray-900 mb-2">Kunne ikke laste attest</h2>
          <p className="text-kf-gray-600 mb-6">{error || 'Attest ikke funnet'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Tilbake til oversikt
          </button>
        </div>
      </div>
    );
  }

  const isExpired = certificate.valid_until && new Date(certificate.valid_until) < new Date();
  const isRevoked = certificate.status === 'revoked';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* EDCI Viewer Loading Overlay */}
      {viewerLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center">
            {/* Multi-color spinner like EDCI Viewer */}
            <div className="relative w-16 h-16 mb-4">
              <div className="absolute inset-0 border-4 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
              <div className="absolute inset-1 border-4 border-transparent border-t-red-500 rounded-full animate-spin" style={{ animationDuration: '0.8s' }}></div>
              <div className="absolute inset-2 border-4 border-transparent border-t-blue-500 rounded-full animate-spin" style={{ animationDuration: '0.6s' }}></div>
            </div>
            <p className="text-gray-700 font-medium">Genererer signert attest...</p>
            <p className="text-gray-500 text-sm mt-1">Åpner EU-validering</p>
          </div>
        </div>
      )}

      {/* Header with Actions */}
      <div className="card">
        <div className="flex flex-col mb-6">
          {/* Status badge - mobile: above title right-aligned, desktop: inline */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-kf-gray-900">{certificate.title}</h1>
            {certificate.status === 'active' && !isExpired && (
              <span className="self-end sm:self-auto mb-2 sm:mb-0 px-3 py-1 bg-kf-blue-100 text-kf-blue-800 text-sm font-semibold rounded-full flex items-center gap-1 whitespace-nowrap">
                <CheckCircle className="w-4 h-4" />
                Aktiv
                </span>
              )}
            {isExpired && (
              <span className="self-end sm:self-auto mb-2 sm:mb-0 px-3 py-1 bg-kf-orange-100 text-kf-orange-800 text-sm font-semibold rounded-full whitespace-nowrap">
                Utløpt
              </span>
            )}
            {isRevoked && (
              <span className="self-end sm:self-auto mb-2 sm:mb-0 px-3 py-1 bg-kf-error-100 text-kf-error-800 text-sm font-semibold rounded-full whitespace-nowrap">
                Tilbakekalt
              </span>
            )}
          </div>
          <p className="text-kf-gray-600 font-mono text-sm">
            Attestnummer: {certificate.certificate_number}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              className={`btn-primary flex items-center gap-2 ${pdfLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
              {pdfLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Genererer PDF-fil...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Last ned signert PDF
                </>
              )}
            </button>
            <button
              onClick={handleToggleShare}
              disabled={shareLoading}
              className={`btn-secondary flex items-center gap-2 ${shareLoading ? 'opacity-50' : ''}`}
            >
              <Share2 className="w-4 h-4" />
              {certificate.share_enabled ? 'Skjul deling' : 'Aktiver deling'}
            </button>
            {certificate.share_enabled && (
              <button onClick={handleOpenVerification} className="btn-secondary flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Offentlig verifisering
              </button>
            )}

            {/* Wallet buttons - Platform-specific */}
            {(() => {
              const ua = navigator.userAgent;
              const isIOS = /iPhone|iPad|iPod/.test(ua);
              const isAndroid = /Android/.test(ua);
              const isMobile = isIOS || isAndroid;

              return (
                <>
                  {/* Apple Wallet - Show on iOS and desktop */}
                  {(isIOS || !isMobile) && (
                    <button
                      onClick={handleAppleWallet}
                      disabled={appleWalletLoading}
                      className={`bg-black hover:bg-gray-800 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors ${appleWalletLoading ? 'opacity-70 cursor-wait' : ''}`}
                      title="Legg til i Apple Wallet"
                    >
                      {appleWalletLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                      )}
                      {appleWalletLoading ? 'Genererer...' : 'Legg til Apple Wallet'}
                    </button>
                  )}

                  {/* Google Wallet - Show on Android and desktop */}
                  {(isAndroid || !isMobile) && (
                    <button
                      onClick={handleGoogleWallet}
                      disabled={googleWalletLoading}
                      className={`bg-[#1a73e8] hover:bg-[#1557b0] text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors ${googleWalletLoading ? 'opacity-70 cursor-wait' : ''}`}
                      title="Legg til i Google Wallet"
                    >
                      {googleWalletLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21.5 6h-19C1.67 6 1 6.67 1 7.5v9c0 .83.67 1.5 1.5 1.5h19c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5zM12 15c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
                        </svg>
                      )}
                      {googleWalletLoading ? 'Genererer...' : 'Legg til Google Wallet'}
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          {/* Europass Export */}
          <div className="p-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
            <p className="text-sm font-semibold text-kf-blue-900 mb-2">🇪🇺 Europass Digital Credentials (EDC)</p>
            <p className="text-xs text-kf-blue-700 mb-3">
              Eksporter attesten som W3C Verifiable Credential for europeisk anerkjennelse
            </p>

            {/* Two boxes side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Green box - Download signed credential */}
              <div className="p-3 bg-green-50 border-2 border-green-500 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-700" />
                  <p className="text-sm font-bold text-green-900">Digitalt Signert</p>
                </div>
                <p className="text-xs text-green-700 mb-3">
                  Offisiell EDC signert med kvalifisert elektronisk segl (eIDAS)
                </p>
                <button
                  onClick={handleDownloadSignedEuropass}
                  disabled={europassLoading}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
                >
                  {europassLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Genererer...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Last ned EDC
                    </>
                  )}
                </button>

                {/* Success message */}
                {europassSuccess && (
                  <div className="mt-2 p-2 bg-green-100 border border-green-400 rounded text-xs text-green-800">
                    ✓ Signert credential lastet ned
                  </div>
                )}
              </div>

              {/* Blue box - EU Validation */}
              <div className="p-3 bg-kf-blue-50 border-2 border-[#003399] rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <EUFlagIcon className="w-5 h-5" />
                  <p className="text-sm font-bold text-[#003399]">EU Validering</p>
                </div>
                <p className="text-xs text-kf-blue-700 mb-3">
                  Verifiser attesten i offisiell EU Europass Viewer
                </p>
                <button
                  onClick={handleOpenEDCViewer}
                  disabled={viewerLoading}
                  className="w-full bg-[#003399] hover:bg-[#002266] disabled:opacity-70 text-white font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
                >
                  {viewerLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Åpner...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      Åpne i EU Viewer
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-kf-blue-200">
              <p className="text-xs text-kf-blue-600">
                💡 <strong>Hva er EDC?</strong> European Digital Credentials er en W3C-standard for digitale kvalifikasjoner som kan brukes i hele Europa.
              </p>
            </div>
          </div>

                  </div>

        {/* Share Link Display */}
        {certificate.share_enabled && certificate.public_share_code && (
          <div className="mt-4 p-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
            <p className="text-sm font-semibold text-kf-blue-900 mb-2">Offentlig delingslenke:</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <code className="flex-1 p-2 bg-white border border-kf-blue-300 rounded text-sm font-mono text-kf-blue-700 break-all overflow-hidden">
                {window.location.origin}/share/{certificate.public_share_code}
              </code>
              <button
                onClick={handleCopyShareLink}
                className="btn-secondary flex items-center gap-2 justify-center sm:justify-start whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-kf-success-600" />
                    Kopiert!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Kopier
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Certificate Details */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4">Attestdetaljer</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1">Utstedt av</p>
            <p className="text-kf-gray-900">{certificate.member_name}</p>
            <p className="text-sm text-kf-gray-600">{certificate.association_name}</p>
          </div>
          {certificate.sensor_name && certificate.sensor_name !== 'Sensor' && (
            <div>
              <p className="text-sm font-semibold text-kf-gray-700 mb-1">Sensor</p>
              <p className="text-kf-gray-900">{certificate.sensor_name}</p>
            </div>
          )}
          {certificate.assessment_form_label && (() => {
            // v6.7.0 — vis label på kandidatens valgte språk
            const useEn = candidateLanguage === 'en';
            const label = useEn && certificate.assessment_form_label_en
              ? certificate.assessment_form_label_en
              : certificate.assessment_form_label;
            return (
              <div>
                <p className="text-sm font-semibold text-kf-gray-700 mb-1">
                  {useEn ? 'Assessment method' : 'Vurderingsform'}
                </p>
                <p className="text-kf-gray-900">{label}</p>
                {certificate.assessment_form_uri && (
                  <a
                    href={certificate.assessment_form_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-kf-blue-600 hover:underline"
                    title={useEn ? 'Open SKOS definition' : 'Åpne SKOS-definisjon av vurderingsmetoden'}
                  >
                    {useEn ? 'About this assessment method →' : 'Om denne vurderingsformen →'}
                  </a>
                )}
              </div>
            );
          })()}
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Utstedelsesdato
            </p>
            <p className="text-kf-gray-900">
              {new Date(certificate.issue_date).toLocaleDateString('no-NO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Gyldig til
            </p>
            <p className="text-kf-gray-900">
              {certificate.valid_until
                ? new Date(certificate.valid_until).toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : 'Ingen utløpsdato'}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Kompetansepoeng (ECTS)
            </p>
            <p className="text-kf-gray-900">{certificate.ects_credits}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              EQF-nivå
            </p>
            <p className="text-kf-gray-900">Nivå {certificate.eqf_level}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Visninger
            </p>
            <p className="text-kf-gray-900">{certificate.view_count}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700 mb-1 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Verifikasjoner
            </p>
            <p className="text-kf-gray-900">{certificate.verification_count}</p>
          </div>
        </div>
      </div>

      {/* Assessment Result */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4">Vurderingsresultat</h2>
        <div className="flex items-center gap-4">
          <div className={`px-6 py-3 rounded-lg ${
            certificate.assessment_result.passed
              ? 'bg-kf-success-100 text-kf-success-800'
              : 'bg-kf-error-100 text-kf-error-800'
          }`}>
            <p className="text-sm font-semibold">Status</p>
            <p className="text-2xl font-bold">
              {certificate.assessment_result.passed ? 'BESTÅTT' : 'IKKE BESTÅTT'}
            </p>
          </div>
          <div className="px-6 py-3 bg-kf-gray-100 rounded-lg">
            <p className="text-sm font-semibold text-kf-gray-700">Poengsum</p>
            <p className="text-2xl font-bold text-kf-gray-900">
              {certificate.assessment_result.score}%
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      {certificate.description && (
        <div className="card">
          <h2 className="text-xl font-bold text-kf-gray-900 mb-4">Beskrivelse</h2>
          <p className="text-kf-gray-700 whitespace-pre-wrap">{certificate.description}</p>
        </div>
      )}

      {/* Task #192: Modul-omfang når attest er for subset av studieplan */}
      {Array.isArray(certificate.module_ids) && certificate.module_ids.length > 0 && (
        <div className="card bg-kf-blue-50 border border-kf-blue-200">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-kf-blue-800 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h2 className="text-base font-semibold text-kf-blue-950 mb-1">
                Attesten dekker {certificate.module_ids.length} av modulene i studieplanen
              </h2>
              <p className="text-sm text-kf-gray-700">
                Læringsutbyttene som vises nedenfor reflekterer kun de modulene som ble prøvd i eksamen.
                Den fulle studieplanen kan ha flere moduler og bredere kompetansemål.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Learning Outcomes */}
      {certificate.learning_outcomes && (
        <div className="card">
          <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Læringsutbytter
          </h2>
          <div className="space-y-4">
            {certificate.learning_outcomes.knowledge && certificate.learning_outcomes.knowledge.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-2">Kunnskap</h3>
                <ul className="list-disc list-inside space-y-1 text-kf-gray-700">
                  {certificate.learning_outcomes.knowledge.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {certificate.learning_outcomes.skills && certificate.learning_outcomes.skills.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-2">Ferdigheter</h3>
                <ul className="list-disc list-inside space-y-1 text-kf-gray-700">
                  {certificate.learning_outcomes.skills.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {certificate.learning_outcomes.competence && certificate.learning_outcomes.competence.length > 0 && (
              <div>
                <h3 className="font-semibold text-kf-gray-900 mb-2">Generell kompetanse</h3>
                <ul className="list-disc list-inside space-y-1 text-kf-gray-700">
                  {certificate.learning_outcomes.competence.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Code */}
      <div className="card text-center">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4">QR-kode for verifisering</h2>
        <div className="inline-block p-4 bg-white border-2 border-kf-gray-300 rounded-lg">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(certificate.verification_url)}`}
            alt="QR-kode for verifisering"
            className="w-48 h-48"
          />
        </div>
        <p className="text-sm text-kf-gray-600 mt-3">
          Skann denne koden for å verifisere attestens ekthet
        </p>
      </div>
    </div>
  );
}
